import React from 'react';

import {EmptyStateFallbackMessage} from '@netx/core-components/EmptyStateFallbackMessage';
import {faUserShield as PermissionsIcon} from '@fortawesome/pro-solid-svg-icons';

import type {AttributePermissionIndexed} from '../../types';
import {PERMISSION_MESSAGES} from '../../locales';

import VirtualizedList, {
	type VirtualizedListProps,
} from '@netx/core-components/VirtualizedList';

import VirtualizedPermissionListItem, {
	type VirtualizedPermissionListItemDataProps,
} from '../VirtualizedListItem';

type OurVirtualizedListProps<
	P extends VirtualizedPermissionListItemDataProps = VirtualizedPermissionListItemDataProps,
> = VirtualizedListProps<AttributePermissionIndexed, P>;

export interface VirtualizedPermissionsListProps<
	P extends VirtualizedPermissionListItemDataProps = VirtualizedPermissionListItemDataProps,
> extends Omit<
		OurVirtualizedListProps<P>,
		'ListItemComponent' | 'ListItemComponentProps'
	> {
	// Allow for the passing of different components so long as their props
	// are compatible with the base `VirtualizedPermissionListItem`
	ListItemComponent?: OurVirtualizedListProps<P>['ListItemComponent'];
	ListItemComponentProps: Required<
		OurVirtualizedListProps<P>
	>['ListItemComponentProps'];
}

const VirtualizedPermissionsList = <
	P extends VirtualizedPermissionListItemDataProps = VirtualizedPermissionListItemDataProps,
>({
	ListItemComponent = VirtualizedPermissionListItem,
	ListItemComponentProps,
	...props
}: VirtualizedPermissionsListProps<P>) => {
	return (
		<>
			{!props.dataReady || props.total > 0 ? (
				<VirtualizedList<AttributePermissionIndexed, P>
					useIsScrolling
					innerElementType="ul"
					{...props}
					ListItemComponent={ListItemComponent}
					ListItemComponentProps={ListItemComponentProps}
				/>
			) : (
				<EmptyStateFallbackMessage
					icon={PermissionsIcon}
					title={PERMISSION_MESSAGES.NO_PERMISSIONS}
				/>
			)}
		</>
	);
};

export default React.memo(VirtualizedPermissionsList) as <
	P extends VirtualizedPermissionListItemDataProps = VirtualizedPermissionListItemDataProps,
>(
	props: Omit<VirtualizedPermissionsListProps<P>, 'classes'>,
) => JSX.Element;
